import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { LoadingButton } from '@mui/lab'
import { Grid, Typography } from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DividerHeading } from '@ui2/structure'
import { Form, UnitConvertedField } from '@ui/forms'
import { enqueueSnackbar } from '@ui/snackbar'
import { useResultsInvalidation } from '@editorHooks'
import { getSimplifiedSeismicModelInputs, getSimplifiedSeismicResults } from '@queries'
import { postSimplifiedSeismicModelInputs } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import useStoreysForSeismic from '../../hooks/useStoreysForSeismic'
import CentroidFieldLabel from './components/CentroidFieldLabel'
import SeismicCentroidSceneMarkers from './components/SeismicCentroidSceneMarkers'
import { SEISMIC_MASS_COLOR, SEISMIC_STIFFNESS_COLOR } from './constants'
import { inputsPerStoreySchema } from './schema'

const SimplifiedSeismicModelInputsForm = (): ReactElement => {
  const { projectId }: { projectId?: string } = useParams()

  const invalidateResultsQueries = useResultsInvalidation()
  const queryClient = useQueryClient()

  const { data: seismicModelInputs } = useQuery({
    queryKey: getSimplifiedSeismicModelInputs.getKey(projectId),
    queryFn: () => getSimplifiedSeismicModelInputs.request(projectId),
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler laden der Modelldaten'), {
        variant: 'error',
      })
    },
    refetchOnMount: true,
  })

  const storeys = useStoreysForSeismic()

  const { isLoading: isSaving, mutate } = useMutation(
    (data: SeismicSettings) => postSimplifiedSeismicModelInputs.request(projectId, data),
    {
      onSuccess: () => {
        invalidateResultsQueries(projectId as string)
        queryClient.invalidateQueries(getSimplifiedSeismicModelInputs.getKey(projectId))
        queryClient.invalidateQueries(getSimplifiedSeismicResults.getKey(projectId))
        enqueueSnackbar('Modelldaten erfolgreich gespeichert und Ergebnisse berechnet', {
          variant: 'success',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Modelldaten'), {
          variant: 'error',
        })
      },
    },
  )

  const defaultValues = useMemo(() => {
    const schemaDefault = inputsPerStoreySchema.getDefault()
    if (!seismicModelInputs) {
      return schemaDefault
    }
    return { data: seismicModelInputs }
  }, [seismicModelInputs])

  if (!seismicModelInputs) {
    return (
      <Typography fontSize={12}>
        Noch keine Modell-Daten verfügbar. Zuerst die Konfiguration speichern.
      </Typography>
    )
  }

  return (
    <Form
      validationSchema={inputsPerStoreySchema}
      defaultValues={defaultValues}
      onSubmit={data => {
        // Use .data because defining an array as root type seems not possible
        // with the yup validation
        mutate(data.data)
      }}
      enableReinitialize
    >
      <SeismicCentroidSceneMarkers />

      {storeys.map(storey => (
        <Grid container columnSpacing={1} rowSpacing={2} key={`inputs-${storey}`}>
          <Grid item xs={12}>
            <DividerHeading spacingTop={0.5}>{storeyToLabel(String(storey))}</DividerHeading>
          </Grid>

          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel={<CentroidFieldLabel color={SEISMIC_MASS_COLOR}>Masse</CentroidFieldLabel>}
              name={`data.${storey}.mass_point.mass`}
              unitLabel="t"
              conversionFactor={1000}
            />
          </Grid>
          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel={
                <CentroidFieldLabel color={SEISMIC_MASS_COLOR}>Massenschwerp. X</CentroidFieldLabel>
              }
              name={`data.${storey}.mass_point.location.x`}
              unitLabel="m"
              conversionFactor={1}
            />
          </Grid>
          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel={
                <CentroidFieldLabel color={SEISMIC_MASS_COLOR}>Massenschwerp. Y</CentroidFieldLabel>
              }
              name={`data.${storey}.mass_point.location.y`}
              unitLabel="m"
              conversionFactor={1}
            />
          </Grid>

          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel="Stockwerkshöhe"
              name={`data.${storey}.height`}
              unitLabel="m"
              conversionFactor={1}
            />
          </Grid>
          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel={
                <CentroidFieldLabel color={SEISMIC_STIFFNESS_COLOR}>
                  Steifigkeitsm. X
                </CentroidFieldLabel>
              }
              name={`data.${storey}.stiffness_point.location.x`}
              unitLabel="m"
              conversionFactor={1}
            />
          </Grid>
          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel={
                <CentroidFieldLabel color={SEISMIC_STIFFNESS_COLOR}>
                  Steifigkeitsm. Y
                </CentroidFieldLabel>
              }
              name={`data.${storey}.stiffness_point.location.y`}
              unitLabel="m"
              conversionFactor={1}
            />
          </Grid>

          <Grid item xs={4} />
          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel={
                <CentroidFieldLabel color={SEISMIC_STIFFNESS_COLOR}>
                  <span>
                    S<sub>x</sub>
                  </span>
                </CentroidFieldLabel>
              }
              name={`data.${storey}.stiffness_point.stiffness.x`}
              unitLabel="MN/m"
              conversionFactor={1000000}
              // Storey stiffnesses run into thousands of kN/m; MN/m keeps the field
              // short, and the third decimal preserves 1 kN/m resolution on edit.
              inputProps={{ decimalScale: 3, step: 0.001, min: 0 }}
            />
          </Grid>
          <Grid item xs={4}>
            <UnitConvertedField
              inputLabel={
                <CentroidFieldLabel color={SEISMIC_STIFFNESS_COLOR}>
                  <span>
                    S<sub>y</sub>
                  </span>
                </CentroidFieldLabel>
              }
              name={`data.${storey}.stiffness_point.stiffness.y`}
              unitLabel="MN/m"
              conversionFactor={1000000}
              // Storey stiffnesses run into thousands of kN/m; MN/m keeps the field
              // short, and the third decimal preserves 1 kN/m resolution on edit.
              inputProps={{ decimalScale: 3, step: 0.001, min: 0 }}
            />
          </Grid>
        </Grid>
      ))}

      <Grid container paddingTop={1}>
        <Grid item xs={12}>
          <LoadingButton
            loading={isSaving}
            variant="contained"
            size="small"
            type="submit"
            data-cy="save-simplified-seismic-input-btn"
          >
            Speichern und Ersatzkräfte berechnen
          </LoadingButton>
        </Grid>
      </Grid>
    </Form>
  )
}

export default SimplifiedSeismicModelInputsForm
